import { useNavigate } from 'react-router-dom';
import { useCallback, type ReactNode } from 'react';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import { useResponsiveImage } from '@/utils/useResponsiveImage';
import Button from '@/components/Button';
import CoverImage from '@/components/CoverImage';
import ProgressBar from '@/components/ProgressBar';
import MediaDuration from '@/components/MediaDuration';
import { formatDurationFromSeconds } from '@/utils/date';
import isPodcastDone from '../../utils/isPodcastDone';
import type { Podcast } from '@edwin-group/people-graphql';

interface PodcastCardProps {
  podcast: Podcast;
  watchButtonLabel?: string | null;
  watchAgainButtonLabel?: string | null;
  compact?: boolean;
  renderBookmarkAction?: ReactNode;
}

const PodcastCard = ({
  podcast,
  watchButtonLabel,
  watchAgainButtonLabel,
  compact = false,
  renderBookmarkAction,
}: PodcastCardProps) => {
  const navigate = useNavigate();

  const image = {
    url: podcast.cover?.url || '',
    alternativeText: podcast.cover?.alternativeText || '',
  };
  const { src: imageSrc } = useResponsiveImage(image, 'medium');

  type PodcastWithAlternatives = Podcast & {
    podcastSlug?: string;
  };
  const podcastWithAlternatives = podcast as PodcastWithAlternatives;
  const slug = podcast.slug || podcastWithAlternatives.podcastSlug || '';

  const handleNavigate = useCallback(() => {
    if (!slug) return;
    navigate(`/podcasts/${slug}`);
  }, [navigate, slug]);

  const { isDone, progress } = isPodcastDone(podcast);
  const listening = podcast.listenings?.[0];
  const duration = listening?.duration != null ? formatDurationFromSeconds(Number(listening.duration)) : null;
  const buttonLabel = isDone ? watchAgainButtonLabel || 'Watch Again' : watchButtonLabel || 'Play';
  const showProgress = true;

  return (
    <article
      className="flex flex-col gap-sh-16 rounded-sh-16 border border-teal-accent bg-light-green p-sh-16 transition-colors md:flex-row md:gap-sh-25"
      data-testid="podcast-card">
      <button
        type="button"
        onClick={handleNavigate}
        className="relative w-full overflow-hidden rounded-t-sh-16 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 md:w-[359px] md:flex-shrink-0 md:rounded-sh-16"
        aria-label={`${buttonLabel}: ${podcast.title || 'Podcast'}`}>
        <CoverImage
          src={imageSrc}
          alt={image.alternativeText || podcast.title || 'Podcast cover'}
          className="w-full md:w-[359px]"
        />
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div>
          <h3 className="mb-[8px] font-haffer text-sh-20-30 font-bold text-black">{podcast.title || ''}</h3>
          {!compact && podcast.briefDescription && (
            <p className="mt-1 line-clamp-2 font-haffer text-sh-sm !font-normal text-black">
              {podcast.briefDescription}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {showProgress && <ProgressBar value={progress || 0} />}
          {duration && <MediaDuration duration={duration} />}
          <div className="flex flex-wrap items-center gap-2">
            <Button theme="outline-accent" onClick={handleNavigate} aria-label={buttonLabel} className="self-start">
              <span>{buttonLabel}</span>
              <PlayCircleIcon className="h-5 w-5 shrink-0" stroke="#E3786D" />
            </Button>
            {renderBookmarkAction}
          </div>
        </div>
      </div>
    </article>
  );
};

export default PodcastCard;
